import { TIPOS_ALVO_IMPACTO, alvoImpacto, type TipoAlvoImpacto } from './alvosImpacto'
import { colisao, type TipoColisao } from './timelineCinematica'
import type { Vetor3 } from '../geometria/vetor3'

/**
 * A natureza do evento e o mecanismo de cada contato, como DADO.
 *
 * Mora fora de `timelineCinematica` e de `alvosImpacto` porque junta os dois — o
 * tipo de colisão e o objeto atingido — e nenhum dos dois precisa aprender sobre
 * o outro para continuar fazendo o que já faz.
 */

/** Como a energia chega ao corpo em cada um dos quatro impactos da aula. */
export type MecanismoImpacto = 'contatoDireto' | 'desaceleracao' | 'conceitual'

export interface DescricaoMecanismo {
  id: MecanismoImpacto
  nome: string
  leitura: string
}

export const MECANISMOS: Record<MecanismoImpacto, DescricaoMecanismo> = {
  contatoDireto: {
    id: 'contatoDireto',
    nome: 'contato direto',
    leitura:
      'O corpo encontra uma superfície e é detido por ela. É o único mecanismo que a simulação ' +
      'consegue medir: há um alvo com nome, uma velocidade relativa e uma energia.',
  },
  desaceleracao: {
    id: 'desaceleracao',
    nome: 'desaceleração',
    leitura:
      'A energia chega sem que haja contato novo: a estrutura para, o que está dentro continua. ' +
      'É o mecanismo que a retenção converte em carga distribuída em vez de impacto localizado.',
  },
  conceitual: {
    id: 'conceitual',
    nome: 'explicação conceitual',
    leitura:
      'Etapa explicada em aula, não simulada. O simulador não modela órgãos nem diagnostica lesões.',
  },
}

export const mecanismo = (id: MecanismoImpacto): DescricaoMecanismo => MECANISMOS[id]

/**
 * Colisão ou choque — e `null` quando não é nem um nem outro.
 *
 * Tombamento e capotamento declaram `direcaoImpacto: null` porque ali não há
 * objeto atingido: o que inicia a rotação é resistência lateral no solo. Inventar
 * uma classificação para esses dois seria afirmar um fato que a própria definição
 * do evento nega — o certo é não informar.
 */
export function classificacaoDoEvento(
  tipoColisao: TipoColisao,
  alvo: TipoAlvoImpacto,
): 'colisão' | 'choque' | null {
  return colisao(tipoColisao).direcaoImpacto === null ? null : alvoImpacto(alvo).classificacao
}

/** Este tipo de colisão tem um objeto atingido cuja escolha faz sentido oferecer. */
export const temAlvoExterno = (tipoColisao: TipoColisao): boolean =>
  colisao(tipoColisao).direcaoImpacto !== null

/**
 * O que pode ser atingido em cada tipo. Abalroamento é, por definição, entre
 * dois veículos em movimento; a lateral contra objeto fixo é contra poste ou
 * muro. Os demais aceitam os três alvos.
 */
export function alvosDoTipo(tipoColisao: TipoColisao): readonly TipoAlvoImpacto[] {
  if (tipoColisao === 'abalroamento') return ['outroVeiculo']
  if (tipoColisao === 'lateralContraObjeto') return ['poste', 'muro']
  return TIPOS_ALVO_IMPACTO
}

/** Alvo em vigor: o escolhido, se o tipo o aceita; senão o primeiro permitido. */
export const alvoEfetivo = (tipoColisao: TipoColisao, alvo: TipoAlvoImpacto): TipoAlvoImpacto =>
  alvosDoTipo(tipoColisao).includes(alvo) ? alvo : alvosDoTipo(tipoColisao)[0]!

const direcaoEhFrontal = (direcao: Vetor3 | null): boolean =>
  !!direcao && Math.abs(direcao.z) > Math.abs(direcao.x) && direcao.z < 0

const direcaoEhTraseira = (direcao: Vetor3 | null): boolean =>
  !!direcao && Math.abs(direcao.z) > Math.abs(direcao.x) && direcao.z > 0

/**
 * O evento tem algum contato frontal — o principal ou, no engavetamento, o
 * primeiro. Baseada no DADO (a direção), não no id do tipo, pela mesma razão
 * de `temAlvoExterno`: um `tipoColisao === 'frontal'` fixo deixava de fora o
 * `frontalComTraseira`, que também tem uma fase frontal.
 */
export function haContatoFrontal(tipoColisao: TipoColisao): boolean {
  const def = colisao(tipoColisao)
  return direcaoEhFrontal(def.direcaoImpacto) || direcaoEhFrontal(def.impactoSecundario?.direcaoImpacto ?? null)
}

/** O mesmo que `haContatoFrontal`, para o contato traseiro. */
export function haContatoPelaTraseira(tipoColisao: TipoColisao): boolean {
  const def = colisao(tipoColisao)
  return direcaoEhTraseira(def.direcaoImpacto) || direcaoEhTraseira(def.impactoSecundario?.direcaoImpacto ?? null)
}

export interface LeituraDoTipoDeImpacto {
  titulo: string
  classificacao: 'colisão' | 'choque' | null
  leitura: string
}

/** Cabeçalho do evento: o que é, contra o quê, e por que recebe esse nome. */
export function leituraDoTipoDeImpacto(
  tipoColisao: TipoColisao,
  alvo: TipoAlvoImpacto,
): LeituraDoTipoDeImpacto {
  const definicao = colisao(tipoColisao)
  const classificacao = classificacaoDoEvento(tipoColisao, alvo)

  if (classificacao === null) {
    return {
      titulo: definicao.nome,
      classificacao: null,
      leitura:
        'Não é colisão nem choque: nenhum objeto foi atingido. O que inicia a rotação é ' +
        'resistência lateral no solo, e por isso não há direção de impacto a mostrar.',
    }
  }

  const externo = alvoImpacto(alvo)
  return {
    // sem repetir a palavra que já vem em `classificacao`: o painel imprime
    // "colisão — contra outro veículo", e não "colisão — colisão frontal — ..."
    titulo: `contra ${externo.nome.toLocaleLowerCase('pt-BR')}`,
    classificacao,
    leitura:
      classificacao === 'colisão'
        ? 'Colisão: os dois corpos estavam em movimento, e o que importa é a velocidade relativa entre eles.'
        : 'Choque: o obstáculo está parado e não cede. Toda a variação de velocidade é do veículo.',
  }
}
